import { memo, useCallback } from 'react'
import { XMarkIcon, SparklesIcon } from '@heroicons/react/24/outline'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'

// Service-category slugs — shown neutral copy ("what you received")
const SERVICE_CATEGORIES = new Set([
  'service', 'cleaning', 'cleaner', 'care', 'education',
  'tutor', 'creative', 'trade', 'trades', 'mutual_aid',
])

function formatRelativeDate(iso, t) {
  const diff = Date.now() - new Date(iso).getTime()
  const hrs = Math.floor(diff / 3600000)
  if (hrs < 1) return t('common.justNow', 'just now')
  if (hrs < 24) return t('common.hoursAgo', '{{n}}h ago', { n: hrs })
  const days = Math.floor(hrs / 24)
  return t('common.daysAgo', '{{n}}d ago', { n: days })
}

/**
 * EnrichmentNudgeCard - "You spotted [Entity] X ago. Share what you experienced?"
 *
 * Shown inside ProximityNotificationStack at lowest priority.
 * Tapping CTA navigates to the entity's Experiences tab.
 * Dismissal persists via the parent hook (localStorage).
 *
 * @param {Object}   props
 * @param {Object}   props.nudge       - { sightingId, entityId, entityName, entityCategory, createdAt }
 * @param {Function} props.onDismiss   - Called with sightingId when × is tapped
 * @atomic @idempotent
 */
const EnrichmentNudgeCard = memo(function EnrichmentNudgeCard({ nudge, onDismiss }) {
  const { t } = useTranslation()
  const navigate = useNavigate()

  const isService = SERVICE_CATEGORIES.has((nudge.entityCategory || '').toLowerCase())

  const ctaText = isService
    ? t('enrichment.ctaService', 'Share what you received')
    : t('enrichment.ctaFood', 'Share what you had')

  const relDate = formatRelativeDate(nudge.createdAt, t)

  const handleCta = useCallback(() => {
    navigate(`/entity/${nudge.entityId}?enrichTab=experiences`)
  }, [navigate, nudge.entityId])

  const handleDismiss = useCallback((e) => {
    e.stopPropagation()
    onDismiss(nudge.sightingId)
  }, [onDismiss, nudge.sightingId])

  return (
    <div className="bg-white border border-gray-200 rounded-xl shadow-md px-3 py-3 flex items-start gap-3">
      {/* Icon */}
      <div className="flex-shrink-0 w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center mt-0.5">
        <SparklesIcon className="w-4 h-4 text-primary" />
      </div>

      {/* Content */}
      <div className="flex-1 min-w-0">
        <p className="text-sm font-semibold text-gray-900 truncate">{nudge.entityName}</p>
        <p className="text-xs text-gray-500 mb-2">
          {t('enrichment.youSpotted', 'You spotted this {{ago}}', { ago: relDate })}
        </p>
        <button
          onClick={handleCta}
          onTouchEnd={(e) => { e.preventDefault(); handleCta() }}
          className="text-xs font-medium text-primary hover:underline"
        >
          {ctaText} &rsaquo;
        </button>
      </div>

      {/* Dismiss */}
      <button
        onClick={handleDismiss}
        onTouchEnd={(e) => { e.preventDefault(); handleDismiss(e) }}
        className="flex-shrink-0 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-full p-1 transition-colors"
        aria-label={t('common.dismiss', 'Dismiss')}
      >
        <XMarkIcon className="w-4 h-4" />
      </button>
    </div>
  )
})

export default EnrichmentNudgeCard
